import { FC, useRef, useMemo } from 'react';
import { AppId, capitalize, FCX, usePlatformContext } from '@datassential/platform-ui-lib';
import HomeSectionFooter from '@pages/Home/components/HomeSectionFooter';
import AccessLink from "@components/AccessLink/AccessLink";
import NewTooltip from '@components/new/NewTooltip/NewTooltip';
import useIsTruncated from '@hooks/useIsTruncated';
import Table from '@components/new/Table/Table';
import { iTableConfigItem } from '@components/new/Table/Table.helpers';
import { getFormattedThousand } from '@helpers';
import { SalesFocusIndependent } from '@apiHooks/useSalesFocusData';
import {
    EMPTY_INDEPENDENTS_MESSAGE,
    MORE_RESTAURANTS_LABEL,
} from './HomeSalesFocusChains.constants';
import './SalesFocusIndependentsTable.scss';

interface SalesFocusIndependentsTableProps {
    rows: SalesFocusIndependent[];
    moreRestaurantsUrl: string | null;
}

const PLACEHOLDER = '-';

// "-" when the BE omits the key, sends null, or sends an empty string (all "no data");
// otherwise the value as-is (text or number; 0 renders as "0", not "-").
const orPlaceholder = (value: string | number | null | undefined): string =>
    value === null || value === undefined || value === '' ? PLACEHOLDER : String(value);

const formatPurchasePotential = (value?: number | null): string =>
    value === null || value === undefined ? PLACEHOLDER : `$${getFormattedThousand(value)}`;

const TruncatedCell: FC<{ text: string }> = ({ text }) => {
    const ref = useRef<HTMLSpanElement | null>(null);
    const isTruncated = useIsTruncated(ref, text);
    return (
        <div className="HomeSalesFocusChains__truncated-cell">
            <span ref={ref} className="HomeSalesFocusChains__truncated-text">{text}</span>
            {isTruncated && <NewTooltip position={['center', 'above']}>{text}</NewTooltip>}
        </div>
    );
};

const TruncatedLinkCell: FC<{ text: string; href: string }> = ({ text, href }) => {
    const ref = useRef<HTMLAnchorElement | null>(null);
    const isTruncated = useIsTruncated(ref, text);
    return (
        <div className="HomeSalesFocusChains__truncated-cell">
            <a
                ref={ref}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="HomeSalesFocusChains__chain-link HomeSalesFocusChains__truncated-text"
            >
                {text}
            </a>
            {isTruncated && <NewTooltip position={['center', 'above']}>{text}</NewTooltip>}
        </div>
    );
};

const ColHeader = ({ label, sub }: { label: string; sub: string }) => (
    <span>
        {label}
        <br />
        <span className="HomeSalesFocusChains__col-sub">{sub}</span>
    </span>
);

const SalesFocusIndependentsTable: FCX<SalesFocusIndependentsTableProps> = ({
    rows,
    moreRestaurantsUrl,
}) => {
    const { userInfo: { products } } = usePlatformContext();

    const operatorsBaseUrl = useMemo(
        () => (products[AppId.Operators]?.url ?? '').replace(/\/+$/, ''),
        [products],
    );

    const config: iTableConfigItem[] = useMemo(
        () => [
            {
                title: 'Operator',
                Value: ({ index }) => {
                    const { name, id } = rows[index];
                    if (!name) return <TruncatedCell text={PLACEHOLDER} />;
                    // Only link when we have a real operators base URL + id; otherwise
                    // render plain text (no relative/broken href).
                    return operatorsBaseUrl && id
                        ? <TruncatedLinkCell text={name} href={`${operatorsBaseUrl}/${id}`} />
                        : <TruncatedCell text={name} />;
                },
            },
            {
                title: 'Metro',
                textCentered: true,
                Value: ({ index }) => <TruncatedCell text={orPlaceholder(rows[index].metro)} />,
            },
            {
                title: 'State',
                textCentered: true,
                Value: ({ index }) => <TruncatedCell text={orPlaceholder(rows[index].state)} />,
            },
            {
                title: 'Cuisine',
                textCentered: true,
                Value: ({ index }) => {
                    const cuisine = rows[index].cuisine;
                    return (
                        <TruncatedCell
                            text={cuisine ? capitalize(cuisine) : PLACEHOLDER}
                        />
                    );
                },
            },
            {
                title: 'Est. Annual Sales',
                textCentered: true,
                Value: ({ index }) => (
                    <span>{orPlaceholder(rows[index].estAnnualSales)}</span>
                ),
            },
            {
                title: 'Total Purchase Potential',
                textCentered: true,
                getHtmlAttributes: () => ({ 'data-col': 'pp-total' }),
                Value: ({ index }) => (
                    <span>{formatPurchasePotential(rows[index].totalPurchasePotential)}</span>
                ),
            },
            {
                title: 'Food',
                textCentered: true,
                Value: ({ index }) => (
                    <span>{formatPurchasePotential(rows[index].foodPurchasePotential)}</span>
                ),
            },
            {
                title: 'Beverage',
                textCentered: true,
                Value: ({ index }) => (
                    <span>{formatPurchasePotential(rows[index].beveragePurchasePotential)}</span>
                ),
            },
            {
                title: 'Disposables',
                textCentered: true,
                getHtmlAttributes: (): Record<string, string> => ({ 'data-col': 'pp-disposables' }),
                Value: ({ index }) => (
                    <span>{formatPurchasePotential(rows[index].disposablesPurchasePotential)}</span>
                ),
            },
            {
                title: 'Popularity Score',
                textCentered: true,
                Value: ({ index }) => <span>{orPlaceholder(rows[index].popularityScore)}</span>,
            },
        ],
        [rows, operatorsBaseUrl],
    );

    const moreLink = (
        <HomeSectionFooter>
            <AccessLink
                url={moreRestaurantsUrl}
                text={MORE_RESTAURANTS_LABEL}
                onContactUsClick={() => undefined}
                testId="linkSalesFocusMoreRestaurants"
            />
        </HomeSectionFooter>
    );

    if (rows.length === 0) {
        return (
            <div className="SalesFocusIndependentsTable">
                <div className="HomeSalesFocusChains__empty-state">
                    {EMPTY_INDEPENDENTS_MESSAGE}
                </div>
                {moreLink}
            </div>
        );
    }

    return (
        <div className="SalesFocusIndependentsTable">
            <div className="SalesFocusIndependentsTable__scroll-wrapper">
                <div className="SalesFocusIndependentsTable__pp-header">
                    <div />
                    <div className="SalesFocusIndependentsTable__pp-label">Purchase Potential</div>
                    <div />
                </div>
                <Table
                    config={config}
                    rowsCount={rows.length}
                    hasInnerScroll={false}
                    isCopyButtonHidden
                />
            </div>
            {moreLink}
        </div>
    );
};

export default SalesFocusIndependentsTable;
